/**
 * <newsletter-form> — inline (in-page) Klaviyo newsletter form.
 *
 * Loaded from `sections/section-newsletter.liquid`. Shares the Klaviyo
 * subscription logic with `<newsletter-popup>` via `@theme/newsletter-handler`,
 * but has no popup/scroll/open-close behaviour.
 *
 * Dependencies:
 *   - @theme/delegate
 *   - @theme/form-validator
 *   - @theme/newsletter-handler
 */

import { Delegate } from '@theme/delegate';
import { FormValidator } from '@theme/form-validator';
import { NewsletterHandler } from '@theme/newsletter-handler';

class NewsletterForm extends HTMLElement {
  #initialized = false;
  #delegate = null;
  #validator = null;
  #newsletterHandler = null;
  #unavailableMessage = 'Newsletter signup is currently unavailable';
  #originalViewport = null;

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#delegate = new Delegate(this);

    this.#initNewsletterHandler();
    this.#initFormValidation();
    this.#bindEvents();
  }

  #getNewsletterSettings() {
    const wrapper = this.querySelector('.newsletter__wrapper');
    if (wrapper?.dataset.settings) {
      return JSON.parse(wrapper.dataset.settings);
    }
    console.error('NewsletterForm: settings not found in .newsletter__wrapper[data-settings].');
    return null;
  }

  #initNewsletterHandler() {
    const settings = this.#getNewsletterSettings();
    if (!settings) return;

    this.#unavailableMessage = settings.unavailableMessage || this.#unavailableMessage;
    if (!settings.listId?.trim() || !settings.publicApiKey?.trim()) return;

    this.#newsletterHandler = new NewsletterHandler({
      listId: settings.listId,
      publicKey: settings.publicApiKey,
      onSuccess: () => this.#showSuccessState(),
      onError: (error) => this.#showErrorState(error),
    });
  }

  #initFormValidation() {
    if (typeof FormValidator === 'undefined') return;
    const form = this.querySelector('.newsletter__form');
    if (!form) return;

    this.#validator = new FormValidator(form, {
      errorClass: 'field--error',
      fieldSelector: '.newsletter__email-input-wrapper',
      validateOnBlur: true,
      validateOnInput: true,
      // Pass the element (not a selector) so a coexisting popup form's error
      // wrapper isn't targeted by the global document.querySelector fallback.
      errorOutputSelector: form.querySelector('.newsletter__error-wrapper'),
      showErrorsOnSubmitOnly: false,
    });

    // Replace the browser's native validity message with a concise one.
    const emailInput = form.querySelector('.newsletter__email-input');
    if (emailInput?.id) {
      const message = emailInput.dataset.invalidMessage || 'Invalid e-mail format';
      this.#validator.addValidator(emailInput.id, (value) => {
        // Empty is the submit handler's job (via required); calling it an invalid
        // FORMAT here is what painted an error over the success note (869ebrznn).
        if (!value.trim()) return true;
        return NewsletterHandler.isValidEmail(value) ? true : message;
      });
    }
  }

  #bindEvents() {
    this.#delegate.on('click', '.newsletter__subscribe-button', this.#onSubmit);
    this.#delegate.on('keydown', '.newsletter__email-input', this.#onSubmit);

    this.#delegate.on('change', '.newsletter__consent input[type="checkbox"]', () => {
      if (this.#requiredConsentChecked()) this.#toggleConsentError(false);
    });

    // iOS zooms in when focusing an input whose font-size is < 16px (the email field
    // is 12px / u-p3). Rather than let it zoom in and then reset (which shows a jarring
    // zoom-out), clamp the viewport to scale 1 while the field is focused so no zoom
    // happens at all; restore the original viewport on blur so pinch-zoom stays
    // available (WCAG 1.4.4). touchstart pre-empts the zoom before focus lands;
    // focusin covers keyboard focus. focusout bubbles (blur doesn't), so it delegates.
    this.#delegate.on('touchstart', '.newsletter__email-input', this.#clampViewport);
    this.#delegate.on('focusin', '.newsletter__email-input', this.#clampViewport);
    this.#delegate.on('focusout', '.newsletter__email-input', this.#restoreViewport);
  }

  #isIOS() {
    return (
      /iP(hone|od|ad)/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  #clampViewport = () => {
    if (!this.#isIOS()) return;

    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) return;

    if (this.#originalViewport === null) {
      this.#originalViewport = viewport.getAttribute('content') || 'width=device-width, initial-scale=1';
    }
    // Don't fight an existing scale constraint the theme may have set intentionally.
    if (this.#originalViewport.includes('maximum-scale')) return;

    viewport.setAttribute('content', `${this.#originalViewport}, maximum-scale=1`);
  };

  #restoreViewport = () => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport || this.#originalViewport === null) return;
    viewport.setAttribute('content', this.#originalViewport);
  };

  // Every required consent checkbox (GDPR) must be ticked before submit.
  #requiredConsentChecked() {
    const boxes = this.querySelectorAll(
      '.newsletter__consent input[type="checkbox"][required]',
    );
    return Array.from(boxes).every((box) => box.checked);
  }

  #toggleConsentError(show) {
    const error = this.querySelector('[data-newsletter-consent-error]');
    if (error) error.hidden = !show;
  }

  #onSubmit = async (e) => {
    if (e.type === 'keydown' && (e.key !== 'Enter' || e.isComposing || e.repeat)) return;

    e.preventDefault();

    if (!this.#newsletterHandler) {
      this.#showErrorState(new Error(this.#unavailableMessage));
      return;
    }

    const emailEl = this.querySelector('.newsletter__email-input');
    const email = emailEl ? emailEl.value.trim() : '';

    const emailValid = email !== '' && NewsletterHandler.isValidEmail(email);
    const consentOk = this.#requiredConsentChecked();

    this.#toggleConsentError(!consentOk);
    if (!emailValid || !consentOk) {
      this.#validator?.showAllErrors();
      return;
    }

    try {
      await this.#newsletterHandler.subscribe(email);
    } catch (_error) {
      // Error state is handled through NewsletterHandler.onError callback.
    }
  };

  #showSuccessState() {
    const information = this.querySelector('[data-newsletter-information]');
    const formWrap = this.querySelector('[data-newsletter-form-wrap]');
    const success = this.querySelector('[data-newsletter-success]');

    // Tear the validator down BEFORE blurring (869ebrznn): the blur below fires
    // validateOnBlur, which would write "Invalid e-mail format" into the very
    // .newsletter__error-wrapper sitting next to the success note.
    this.#validator?.clearErrors();
    this.#validator?.destroy();
    this.#validator = null;

    const errorWrapper = this.querySelector('.newsletter__error-wrapper');
    if (errorWrapper) {
      errorWrapper.textContent = '';
    }

    if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    if (information) information.hidden = true;
    if (formWrap) formWrap.hidden = true;
    if (success) {
      success.hidden = false;
      success.setAttribute('aria-live', 'polite');
    }
  }

  #showErrorState(error) {
    // Klaviyo treats an existing subscriber as a 409; show success either way.
    if (error?.message === 'DUPLICATE_EMAIL') {
      this.#showSuccessState();
      return;
    }

    const errorWrapper = this.querySelector('.newsletter__error-wrapper');
    if (!errorWrapper) return;

    // Server message when present, else a defensive hardcoded fallback.
    errorWrapper.textContent = error?.message || 'Something went wrong. Please try again.';
  }

  disconnectedCallback() {
    this.#initialized = false;
    // Restore the viewport if we disconnect while still clamped (field focused).
    this.#restoreViewport();
    this.#delegate?.off();
    this.#delegate = null;
    this.#validator?.destroy();
    this.#validator = null;
    this.#newsletterHandler?.abort();
    this.#newsletterHandler = null;
  }
}

if (!customElements.get('newsletter-form')) {
  customElements.define('newsletter-form', NewsletterForm);
}
